import { useContext } from 'react';
import { Paper, Typography, Divider } from '@mui/material';
import { useItemTooltip, ChartsTooltipContainer } from '@mui/x-charts';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatPercentValue } from '@/utils/numbers';
import type { HorizontalBarDatum } from '../types';
import { BarChartTooltipContext } from './barChartTooltipContext';

const BarChartTooltipContent = () => {
  const config = useContext(BarChartTooltipContext);
  const itemTooltip = useItemTooltip();

  if (config === null || itemTooltip === null) {
    return null;
  }

  const id = itemTooltip.identifier;
  if (!('dataIndex' in id)) return null;
  const dataIndex = (id as { dataIndex?: number }).dataIndex;
  if (dataIndex === undefined) return null;

  const datum = config.data[dataIndex] as HorizontalBarDatum | undefined;
  if (!datum) return null;

  if (config.renderContent) {
    return <>{config.renderContent(datum)}</>;
  }

  const formatCurrency = config.formatCurrency ?? formatNumberToCurrency;

  return (
    <Paper
      elevation={3}
      sx={{ p: 1 }}
    >
      <Typography
        variant='body2'
        sx={{ textAlign: 'left', fontWeight: 'bold' }}
      >
        {datum.name}
      </Typography>
      <Divider sx={{ my: 0.5 }} />
      <Typography
        variant='body2'
        sx={{ textAlign: 'left' }}
      >
        {formatCurrency(datum.rawValue)}
      </Typography>
      <Typography
        variant='body2'
        sx={{ textAlign: 'left' }}
      >
        {formatPercentValue(datum.percentValue)}
      </Typography>
    </Paper>
  );
};

/**
 * Tooltip slot for the horizontal bar chart. The container owns placement, open
 * state and the pointer and touch handling, so none of that is reimplemented
 * here and nothing has to track the cursor in component state.
 */
export const BarChartTooltip = () => (
  <ChartsTooltipContainer trigger='item'>
    <BarChartTooltipContent />
  </ChartsTooltipContainer>
);
